import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import Table from 'Components/Table';
import { Text, Box, Flex } from 'Components/Base';
import { formatQuizFeedAccuracy, formatQuizFeedScore } from 'Domains/adaptiveQuiz';
import { MASTERY_LEVEL } from 'Domains/mastery';

import { getAdaptiveQuizIndividualPath } from '../utils';

const TextWrapper = styled(Box)`
  text-align: center;
  white-space: pre-line;
`;

/**
 * NOTE: react-table renders an empty placeholder cell above each column which is not in a group,
 * so the header of column 1-5 and 10 is moved up to the middle of both header rows,
 * and only the group header (6th cell of the 1st row) has a bottom border.
 */
const AdaptiveQuizTableStyle = styled(Table)`
  & .MuiTableCell-sizeSmall {
    padding: 12px 10px;
    border-right: 1px solid ${({ theme }) => theme.colors.white};
    border-bottom: none;

    &:nth-last-child(1) {
      border-right: none;
    }
  }

  & .MuiTableRow-head .MuiTableCell-sizeSmall {
    padding: 6px 10px;
  }

  & .MuiTableRow-head:nth-child(1) .MuiTableCell-head:nth-child(6) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.white};
  }

  & .MuiTableRow-head:nth-child(2) .MuiTableCell-head {
    &:nth-child(-n + 5),
    &:nth-child(10) {
      > div {
        position: relative;
        top: -18px;
      }
    }
  }
`;

export const TableHead = props => (
  <Text fontWeight={600} style={{ whiteSpace: 'pre-line', textAlign: 'center' }} {...props} />
);

const MasteryCell = ({ value }) => {
  const masteryLevel = MASTERY_LEVEL[value];
  if (!masteryLevel) return <TextWrapper>-</TextWrapper>;
  return (
    <Flex style={{ alignItems: 'center', justifyContent: 'center' }}>
      <Box
        style={{
          backgroundColor: masteryLevel.color,
          width: '14px',
          height: '14px',
          flexShrink: 0,
          borderRadius: '50%',
        }}
      />
      <TextWrapper style={{ marginLeft: '8px' }}>{masteryLevel.name}</TextWrapper>
    </Flex>
  );
};

export const AdaptiveQuizTable = ({ data: { reports = [] }, maxHeight, ...props }) => {
  const { t } = useTranslation('adaptiveQuiz');
  const history = useHistory();
  const location = useLocation();

  const openRounds = report => {
    history.push(
      getAdaptiveQuizIndividualPath({
        pathname: location.pathname,
        queryString: new URLSearchParams(location.search),
        report,
      })
    );
  };

  const columns = [
    {
      id: 'classNo',
      Header: <TableHead>{t('CLASS')}</TableHead>,
      accessor: properties => `${properties.schoolGrade.shortName}/${properties.classNo}`,
      width: 80,
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'60px'}>{value}</TextWrapper>,
    },
    {
      id: 'unit',
      Header: <TableHead>{t('UNIT')}</TableHead>,
      accessor: properties => `${properties.courseName}\n(${properties.courseCode})`,
      disableSortBy: true,
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'160px'}>{value}</TextWrapper>,
    },
    {
      id: 'section',
      Header: <TableHead>{t('SECTION')}</TableHead>,
      accessor: 'lessonName',
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'120px'}>{value}</TextWrapper>,
    },
    {
      id: 'username',
      Header: <TableHead>{t('USERNAME')}</TableHead>,
      accessor: 'classroomUsername',
      Cell: ({ cell: { value } }) => <TextWrapper minWidth={'110px'}>{value}</TextWrapper>,
    },
    {
      id: 'displayName',
      Header: <TableHead>{t('DISPLAY_NAME')}</TableHead>,
      accessor: 'studentName',
      Cell: ({ cell: { value } }) => (
        <TextWrapper minWidth={'160px'} style={{ textAlign: 'left' }}>
          {value}
        </TextWrapper>
      ),
    },
    {
      id: 'questionCount',
      Header: <TableHead>{t('QUESTION_COUNT')}</TableHead>,
      columns: [
        {
          id: 'tryQuiz',
          Header: <TableHead>{t('TRY_QUIZ')}</TableHead>,
          accessor: 'tryQuiz',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => <TextWrapper>{formatQuizFeedScore(value)}</TextWrapper>,
        },
        {
          id: 'tryQuizAccuracy',
          Header: <TableHead>{t('ACCURACY')}</TableHead>,
          accessor: properties => properties.tryQuiz?.accuracy ?? 0,
          Cell: ({ cell: { row } }) => (
            <TextWrapper>{formatQuizFeedAccuracy(row?.original?.tryQuiz)}</TextWrapper>
          ),
        },
        {
          id: 'adaptiveQuiz',
          Header: <TableHead>{t('ADAPTIVE_QUIZ')}</TableHead>,
          accessor: 'adaptiveQuiz',
          disableSortBy: true,
          Cell: ({ cell: { value } }) => <TextWrapper>{formatQuizFeedScore(value)}</TextWrapper>,
        },
        {
          id: 'adaptiveQuizAccuracy',
          Header: <TableHead>{t('ACCURACY')}</TableHead>,
          accessor: properties => properties.adaptiveQuiz?.accuracy ?? 0,
          Cell: ({ cell: { row } }) => (
            <TextWrapper>{formatQuizFeedAccuracy(row?.original?.adaptiveQuiz)}</TextWrapper>
          ),
        },
      ],
    },
    {
      id: 'mastery',
      Header: <TableHead>{t('MASTERY')}</TableHead>,
      accessor: 'mastery',
      disableSortBy: true,
      Cell: ({ cell: { value } }) => <MasteryCell value={value} />,
    },
  ];

  return (
    <AdaptiveQuizTableStyle
      dense
      stickyHeader
      columns={columns}
      data={reports}
      style={{ maxHeight }}
      getRowProps={rowProps => ({
        style: { cursor: 'pointer' },
        hover: true,
        onClick: () => openRounds(rowProps.original),
      })}
      {...props}
    />
  );
};

AdaptiveQuizTable.propTypes = {
  data: PropTypes.shape({
    reports: PropTypes.array.isRequired,
  }),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default AdaptiveQuizTable;
